import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { RxDocument } from 'rxdb'
import { useRxCollection } from 'rxdb-hooks'
import { toast } from 'ui/sonner'
import { create } from 'zustand'

import { Card } from '~/db/types'
import { type CardsGameState, useCardsGameStore } from '~/stores/cards-game'

export const useLastRevertedCardId = create<{ value: string | null }>(() => ({ value: null }))

export const useCardDeletionRevert = () => {
  const collection = useRxCollection<Card>('cards')
  const { t } = useTranslation()

  return useCallback(
    async (card: RxDocument<Card>) => {
      if (!collection) return

      const cardsGameStore = useCardsGameStore.getState().getSerializedState()

      await card.remove()

      toast.info(t('Revert card'), {
        id: `revert-card-${card.id}`,
        action: {
          label: t('Revert'),
          onClick: async () => {
            useLastRevertedCardId.setState({ value: card.id })
            const revertedCard = await collection.upsert(card.toJSON())

            const substitutedRxDocuments: CardsGameState = { ...cardsGameStore }
            // TODO: how to keep it in sync making sure all fields are handled
            substitutedRxDocuments.allCards = substitutedRxDocuments.allCards.map(card =>
              card.id === revertedCard.id ? revertedCard : card,
            )
            substitutedRxDocuments.shuffledCards = substitutedRxDocuments.shuffledCards.map(card =>
              card.id === revertedCard.id ? revertedCard : card,
            )
            substitutedRxDocuments.notGuessedCards = substitutedRxDocuments.notGuessedCards.map(card =>
              card.id === revertedCard.id ? revertedCard : card,
            )

            useCardsGameStore.setState(substitutedRxDocuments)
          },
        },
      })
    },
    [collection, t],
  )
}
